import moment from "moment";
import {getDateRangeByLabel} from "../../components/DateRange";

const LEGACY_BOOL_FILTER_KEYS = [
    "new_school", "creative", "exclusive", "medium", "cf", "cw", "already",
    "undecided_bach", "cert_upgrade", "when_start_upgrade", "military",
] as const;

export type LinkoutPresetSnapshot = {
    dates: Date[];
    data: any[];
    stats: any[];
    filterItems: any[];
    uniqueLeads: boolean;
    minResults: string;
    contactRate: string;
    transferRate: string;
    selectedBrokers: any[];
    selectedPublishers: any[];
    publishersExclude: boolean;
    selectedSchools: any[];
    selectedCompanies: any[];
    selectedPrograms: any[];
    selectedSubjects: any[];
    selectedTraffic: any[];
    selectedWhenStart: any[];
    selectedWorking: any[];
    selectedEdu: any[];
    selectedDegree: any[];
    selectedClasses: any[];
    selectedConvTypes: any[];
    boolFilters: Record<string, boolean>;
    campaignIds: string;
    creativeUrls: string;
    states: string;
    gender: string;
    hoursOldMin: string;
    hoursOldMax: string;
    ageMin: string;
    ageMax: string;
};

type NormalizeOptions = {
    dataOptions: { id: string; value: string }[];
    statOptions: { id: string; value: string }[];
    filterOptions: { id: string; value: string }[];
    brokers: { id: number; value: string }[];
    schools: { id: number; value: string }[];
    companies: { id: string; value: string }[];
    programs: { id: number; value: string }[];
    subjects: { id: number; value: string }[];
    trafficTypes: { id: string; value: string }[];
    whenStart: { id: number; value: string }[];
    working: { id: number; value: string }[];
    eduLevels: { id: number; value: string }[];
    degreeLevels: { id: number; value: string }[];
    classes: { id: number; value: string }[];
    conversionTypes: { id: number; value: string }[];
};

function idsFrom(raw: any): number[] {
    if (!raw) return [];
    const list = Array.isArray(raw) ? raw : String(raw).split(/[,\s]+/);
    return list.map((x) => parseInt(String(x), 10)).filter((n) => !isNaN(n));
}

function pickByIds<T extends { id: number | string }>(all: T[], raw: any, fallbackLabel = "id"): T[] {
    const idList = Array.isArray(raw)
        ? raw.map((x) => (typeof x === "object" ? x.id : x))
        : idsFrom(raw);
    return idList.map((id) => {
        const found = all.find((x) => String(x.id) === String(id));
        return found || ({id, value: String(id)} as T);
    });
}

function parsePresetDates(dates: any): Date[] | null {
    if (Array.isArray(dates) && dates.length === 2) {
        return [moment(dates[0]).toDate(), moment(dates[1]).toDate()];
    }
    const range = dates?.selectedRange ? String(dates.selectedRange) : "";
    // Legacy recomputes named ranges on run; only Custom Range uses saved start/end.
    if (range && range !== "Custom Range") {
        const fromLabel = getDateRangeByLabel(range);
        if (fromLabel) return fromLabel;
    }
    if (dates?.selectedStartDate != null && dates?.selectedEndDate != null) {
        return [moment(dates.selectedStartDate).toDate(), moment(dates.selectedEndDate).toDate()];
    }
    return null;
}

function parseCompanies(raw: any, companies: { id: string; value: string }[]): any[] {
    if (!raw) return [];
    if (Array.isArray(raw)) {
        return raw.map((x) => {
            const name = typeof x === "object" ? x.id ?? x.name ?? x.value : x;
            const found = companies.find((c) => c.id === name || c.value === name);
            return found || {id: String(name), value: String(name)};
        });
    }
    const names = String(raw).split(",").map((x) => x.trim()).filter(Boolean);
    return names.map((name) => companies.find((c) => c.id === name || c.value === name) || {id: name, value: name});
}

function publisherItems(raw: any, legacy: boolean, f: any): any[] {
    const ids = legacy
        ? idsFrom(f.publishers)
        : (Array.isArray(raw.selectedPublishers)
            ? raw.selectedPublishers.map((x: any) => (typeof x === "object" ? x.id : x))
            : idsFrom(raw.selectedPublishers));
    return ids.map((id) => ({id, value: String(id)}));
}

/** Map legacy Angular preset JSON (filters + dates object) into native LO page shape. */
export function normalizeConversionStatPreset(raw: any, opts: NormalizeOptions): LinkoutPresetSnapshot {
    const legacy = !Array.isArray(raw?.dates);
    const f = raw?.filters || {};

    const boolFilters: Record<string, boolean> = {...(raw.boolFilters || {})};
    LEGACY_BOOL_FILTER_KEYS.forEach((key) => {
        if (legacy && f[key] != null) boolFilters[key] = !!f[key];
    });

    let gender = raw.gender ?? "";
    if (legacy && f.gender != null && typeof f.gender === "boolean") {
        gender = "";
    } else if (legacy && typeof f.gender === "string") {
        gender = f.gender;
    }

    const dates = parsePresetDates(raw.dates) || [
        moment().startOf("month").toDate(),
        moment().endOf("day").toDate(),
    ];

    return {
        dates,
        data: opts.dataOptions.filter((x) => (raw.data || []).includes(x.id)),
        stats: opts.statOptions
            .filter((x) => (raw.stats || []).includes(x.id))
            .map((x) => ({id: x.id, value: x.value})),
        filterItems: opts.filterOptions.filter((x) => (raw.filterItems || []).includes(x.id)),
        uniqueLeads: raw.uniqueLeads != null ? !!raw.uniqueLeads : true,
        minResults: String(raw.minResults ?? f.total ?? "1"),
        contactRate: String(raw.contactRate ?? f.contactRate ?? "0.01"),
        transferRate: String(raw.transferRate ?? f.transferRate ?? "0.01"),
        selectedBrokers: pickByIds(opts.brokers, legacy ? f.brokers : raw.selectedBrokers),
        selectedPublishers: publisherItems(raw, legacy, f),
        publishersExclude: !!(raw.publishersExclude ?? f?.publishers_exclude),
        selectedSchools: pickByIds(opts.schools, legacy ? f.schools : raw.selectedSchools),
        selectedCompanies: parseCompanies(legacy ? f.companies : raw.selectedCompanies, opts.companies),
        selectedPrograms: pickByIds(opts.programs, legacy ? f.programs : raw.selectedPrograms),
        selectedSubjects: pickByIds(opts.subjects, legacy ? f.subjects : raw.selectedSubjects),
        selectedTraffic: pickByIds(opts.trafficTypes, legacy ? f.traffic_types : raw.selectedTraffic),
        selectedWhenStart: pickByIds(opts.whenStart, legacy ? f.when_start : raw.selectedWhenStart),
        selectedWorking: pickByIds(opts.working, legacy ? f.working : raw.selectedWorking),
        selectedEdu: pickByIds(opts.eduLevels, legacy ? f.edu_level : raw.selectedEdu),
        selectedDegree: pickByIds(opts.degreeLevels, legacy ? f.degree_level : raw.selectedDegree),
        selectedClasses: pickByIds(opts.classes, legacy ? f.classes : raw.selectedClasses),
        selectedConvTypes: pickByIds(opts.conversionTypes, legacy ? f.conversion_type : raw.selectedConvTypes),
        boolFilters,
        campaignIds: String(legacy ? (f.campaigns ?? "") : (raw.campaignIds ?? "")),
        creativeUrls: String(legacy ? (f.creative_ids ?? "") : (raw.creativeUrls ?? "")),
        states: String(legacy ? (f.states ?? "") : (raw.states ?? "")),
        gender: String(gender),
        hoursOldMin: String(raw.hoursOldMin ?? f?.hours_old_min ?? "0"),
        hoursOldMax: String(raw.hoursOldMax ?? f?.hours_old_max ?? "72"),
        ageMin: String(raw.ageMin ?? f?.age_min ?? "18"),
        ageMax: String(raw.ageMax ?? f?.age_max ?? "99"),
    };
}
